import React, { useState } from 'react';
import { User, Paperclip, Star, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import type { Database } from '../../lib/database.types';

type Note = Database['public']['Tables']['customer_notes']['Row'];

interface CustomerNotesProps {
  notes: Note[];
  onAddNote: (content: string) => Promise<void>;
  onDeleteNote: (id: string) => Promise<void>;
}

export const CustomerNotes: React.FC<CustomerNotesProps> = ({
  notes,
  onAddNote,
  onDeleteNote,
}) => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
  };

  return (
    <>
      <Card className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Internal Notes</h3>
            <p className="text-sm text-gray-500">Notes will only be seen by your team</p>
          </div>
          <Button onClick={() => setShowAddDialog(true)}>Add Note</Button>
        </div>

        <div className="space-y-6">
          {notes.map((note) => (
            <div key={note.id} className="flex gap-4 group">
              <Avatar className="w-10 h-10 flex-shrink-0">
                <AvatarFallback className="bg-blue-100 text-blue-600">
                  <User className="w-5 h-5" />
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 space-y-1">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Team Member</p>
                    <p className="text-sm text-gray-500">{formatDate(note.created_at)}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDeleteNote(note.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mt-2">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.content}</p>
                </div>

                {note.attachments && (
                  <div className="flex items-center gap-2 mt-2">
                    <Paperclip className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-500">2 attachments</span>
                  </div>
                )}

                {note.important && (
                  <div className="flex items-center gap-2 mt-2">
                    <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    <span className="text-sm text-gray-500">Important note</span>
                  </div>
                )}
              </div>
            </div>
          ))}

          {notes.length === 0 && (
            <div className="text-center py-12">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-6 h-6 text-gray-400" />
              </div>
              <h3 className="text-sm font-medium text-gray-900">No notes yet</h3>
              <p className="text-sm text-gray-500 mt-1">Get started by adding a note about this customer.</p>
            </div>
          )}
        </div>
      </Card>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
            <DialogDescription>
              Add a note about this customer. Notes are internal and will only be visible to your team.
            </DialogDescription>
          </DialogHeader>

          {error && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
              {error}
            </div>
          )}

          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setError(null);
              
              const form = e.target as HTMLFormElement;
              const content = (form.elements.namedItem('content') as HTMLTextAreaElement).value;
              
              try {
                await onAddNote(content);
                setShowAddDialog(false);
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to add note');
              }
            }}
          >
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="content">Note Details</Label>
                <Textarea
                  id="content"
                  name="content"
                  required
                  rows={4}
                  placeholder="Enter your note here..."
                  className="resize-none"
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddDialog(false)}
              >
                Cancel
              </Button>
              <Button type="submit">Add Note</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};